import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed } from "@/lib/format";
import { printLedgerReport } from "@/lib/ledgerPrint";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const defaultFromDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function LedgerReport() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const [accountId, setAccountId] = useState(searchParams.get("account_id") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("from_date") || defaultFromDate());
  const [toDate, setToDate] = useState(searchParams.get("to_date") || today());

  const { data: accountsData } = useQuery({
    queryKey: ["accounts", branchId, "ledger"],
    queryFn: async () => {
      const { data: res } = await api.get("/accounts", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const accounts = accountsData?.accounts ?? [];

  useEffect(() => {
    if (!accountId && accounts.length) {
      setAccountId(String(accounts[0].id));
    }
  }, [accounts, accountId]);

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["ledger", branchId, accountId, fromDate, toDate],
    queryFn: async () => {
      const { data: res } = await api.get("/ledger", {
        params: {
          ...reportParams,
          account_id: accountId,
          from_date: fromDate,
          to_date: toDate,
        },
      });
      return res;
    },
    enabled: Boolean(canQuery && accountId && fromDate && toDate),
  });

  const selectedAccount = useMemo(
    () => accounts.find((a) => String(a.id) === String(accountId)),
    [accounts, accountId],
  );

  return (
    <ReportPageWrap>
      <PageHeader
        title="Ledger Report"
        subtitle="Account-wise voucher entries with running balance"
        actions={
          <ReportExportButtons
            disabled={!report}
            title="Ledger Report"
            filename="ledger"
            printTestId="ledger-print"
            onPrint={() => printLedgerReport(report, "Ledger Report", { branchId, allBranches })}
          />
        }
      />

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-5 gap-y-3">
          <div className="md:col-span-2">
            <Label className="text-[12px] text-slate-600 mb-1 block">Account *</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-9" data-testid="ledger-account">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.code} — {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">From Date</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9"
              data-testid="ledger-from"
            />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">To Date</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9"
              data-testid="ledger-to"
            />
          </div>
        </div>
      </Card>

      {selectedAccount && (
        <ReportKpiStrip
          items={[
            {
              label: "Opening",
              value: (
                <span data-testid="ledger-opening">
                  {report ? aed(report.opening_balance) : "—"}
                </span>
              ),
            },
            { label: "Total Debit", value: report ? aed(report.total_debit) : "—" },
            { label: "Total Credit", value: report ? aed(report.total_credit) : "—" },
            {
              label: "Closing",
              value: (
                <span data-testid="ledger-closing">
                  {report ? aed(report.closing_balance) : "—"}
                </span>
              ),
              emphasize: true,
            },
          ]}
        />
      )}

      <ReportTableCard
        title="Ledger Entries"
        actions={
          <div className="flex items-center gap-2">
            {report?.entry_count != null && <Pill>{report.entry_count}</Pill>}
            {(isLoading || isFetching) && (
              <span className="text-[12px] text-slate-500 flex items-center gap-1.5">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </span>
            )}
          </div>
        }
      >
        <table>
          <thead>
            <tr>
              <th className="text-left w-[10%]">Date</th>
              <th className="text-left w-[12%]">Voucher</th>
              <th className="text-left w-[8%]">Type</th>
              <th className="text-left w-[34%]">Particulars</th>
              <th className="text-right w-[12%]">Debit</th>
              <th className="text-right w-[12%]">Credit</th>
              <th className="text-right w-[12%]">Balance</th>
            </tr>
          </thead>
          <tbody>
            {report && (
              <tr className="bg-slate-50/60 italic text-slate-600">
                <td colSpan={4}>Opening balance</td>
                <td />
                <td />
                <td className="text-right font-mono font-semibold">{aed(report.opening_balance)}</td>
              </tr>
            )}
            {!report && !isLoading && (
              <tr>
                <td className="text-center text-slate-500 py-10" colSpan={7}>
                  Select an account and date range to view the ledger.
                </td>
              </tr>
            )}
            {report?.entries?.length === 0 && (
              <tr>
                <td className="text-center text-slate-500 py-8" colSpan={7}>
                  No voucher entries in this period.
                </td>
              </tr>
            )}
            {report?.entries?.map((e, i) => (
              <tr key={e.id} data-testid={`ledger-row-${i}`}>
                <td className="text-slate-700">{e.voucher_date}</td>
                <td className="font-mono text-blue-700">{e.voucher_number}</td>
                <td>
                  <Pill tone="gray">{e.voucher_type}</Pill>
                </td>
                <td className="text-slate-700">
                  {e.narration || e.party_name || "—"}
                  {e.reference ? <span className="text-slate-400"> • {e.reference}</span> : null}
                </td>
                <td className="text-right font-mono">{e.debit > 0 ? aed(e.debit) : ""}</td>
                <td className="text-right font-mono">{e.credit > 0 ? aed(e.credit) : ""}</td>
                <td className="text-right font-mono font-semibold">{aed(e.balance)}</td>
              </tr>
            ))}
          </tbody>
          {report && (
            <tfoot>
              <tr className="bg-slate-50 border-t font-semibold">
                <td className="text-right px-3 py-2" colSpan={4}>Period totals / closing</td>
                <td className="text-right font-mono px-3 py-2">{aed(report.total_debit)}</td>
                <td className="text-right font-mono px-3 py-2">{aed(report.total_credit)}</td>
                <td className="text-right font-mono text-emerald-700 px-3 py-2">{aed(report.closing_balance)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </ReportTableCard>
    </ReportPageWrap>
  );
}
